package dev.danko.roblox_craft_ai.shared.animation

/**
 * The R15 character both the server and the site viewer use: where each joint sits in its parent's frame (studs, Y up,
 * the character faces -Z) and the box of the part that hangs from it. The joints and boxes are those of Roblox's
 * standard R15 rig (the viewer draws its exported parts, models/r15-avatar.glb): shoulders on the edge of the torso,
 * the parts overlapping at elbows, wrists, hips, knees and ankles. One description, so the server's ground and limit
 * checks see exactly the figure the viewer draws and Studio plays.
 */
object R15Skeleton {
    class Segment(
        val joint: String,
        val parent: String?,
        /** Joint point in the parent joint's frame. */
        val pivot: Vec3,
        /** Box of the part hanging from the joint: size and centre in the joint's frame. */
        val size: Vec3,
        val center: Vec3
    )

    val SEGMENTS: List<Segment> = buildList {
        add(Segment("LowerTorso", null, Vec3(0.0, 0.0, 0.0), Vec3(2.0, 0.4, 1.0), Vec3(0.0, 0.2, 0.0)))
        add(Segment("UpperTorso", "LowerTorso", Vec3(0.0, 0.4, 0.0), Vec3(2.0, 1.6, 1.0), Vec3(0.0, 0.8, 0.0)))
        add(Segment("Head", "UpperTorso", Vec3(0.0, 1.6, 0.0), Vec3(1.2, 1.2, 1.2), Vec3(0.0, 0.587, 0.0)))
        for ((side, sx) in listOf("Left" to -1.0, "Right" to 1.0)) {
            add(Segment("${side}UpperArm", "UpperTorso", Vec3(1.0 * sx, 1.363, 0.0), Vec3(1.0, 1.169, 1.0), Vec3(0.5 * sx, -0.394, 0.0)))
            add(Segment("${side}LowerArm", "${side}UpperArm", Vec3(0.5 * sx, -0.727, 0.0), Vec3(1.0, 1.052, 1.0), Vec3(0.0, -0.26, 0.0)))
            add(Segment("${side}Hand", "${side}LowerArm", Vec3(0.0, -0.761, 0.0), Vec3(1.0, 0.3, 1.0), Vec3(0.0, -0.125, 0.0)))
            add(Segment("${side}UpperLeg", "LowerTorso", Vec3(0.5 * sx, 0.0, 0.0), Vec3(1.0, 1.217, 1.0), Vec3(0.0, -0.42, 0.0)))
            add(Segment("${side}LowerLeg", "${side}UpperLeg", Vec3(0.0, -0.8, 0.0), Vec3(1.0, 1.193, 1.0), Vec3(0.0, -0.4, 0.0)))
            add(Segment("${side}Foot", "${side}LowerLeg", Vec3(0.0, -0.95, 0.0), Vec3(1.0, 0.3, 1.0), Vec3(0.0, -0.1, 0.0)))
        }
    }

    val BY_JOINT: Map<String, Segment> = SEGMENTS.associateBy { it.joint }

    /** Height of the soles below the root joint when standing: the floor of the viewer and the ground check. */
    val SOLE_Y: Double = run {
        val foot = BY_JOINT.getValue("RightFoot")
        BY_JOINT.getValue("RightUpperLeg").pivot.y + BY_JOINT.getValue("RightLowerLeg").pivot.y + foot.pivot.y +
            foot.center.y - foot.size.y / 2
    }
}

data class Vec3(val x: Double, val y: Double, val z: Double) {
    operator fun plus(o: Vec3) = Vec3(x + o.x, y + o.y, z + o.z)
    operator fun minus(o: Vec3) = Vec3(x - o.x, y - o.y, z - o.z)
    operator fun times(k: Double) = Vec3(x * k, y * k, z * k)
    fun dot(o: Vec3) = x * o.x + y * o.y + z * o.z
    fun cross(o: Vec3) = Vec3(y * o.z - z * o.y, z * o.x - x * o.z, x * o.y - y * o.x)
    fun length() = kotlin.math.sqrt(dot(this))
    fun normalized(): Vec3 = length().let { if (it < 1e-12) this else this * (1 / it) }
}
